import 'vue-swatches/dist/vue-swatches.min.css'
import './drawing-editor.scss'

import { faCircle, faImage, faSquare, faTimesCircle } from '@fortawesome/free-regular-svg-icons';
import { faArrowLeft, faCheck, faCircleNotch, faLongArrowAltLeft, faPalette, faPen } from '@fortawesome/free-solid-svg-icons';
import { FormApi } from 'final-form';
import { get, set } from 'lodash';
import { first } from 'rxjs/operators';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import Swatches from 'vue-swatches'

import { DefectAnnotation, ThesaurusTermDataStore, thesaurusTermSelectors } from '@skinsoft/entity';
import { preferredTerm } from '@skinsoft/entity/src/thesaurusTerm/selectors';
import { getImageOrientation, MediaTransferHelper } from '@skinsoft/media-transfer-helper';
import { ReactiveVue } from '@skinsoft/reactive-vue';
import { createTranslatedString, EnhancedTextInput, getTranslatedString } from '@skinsoft/ui';
import { Media } from '@skinsoft/ui/src/item-card/media';

import { isImageOrientationAutoManaged } from '@skinsoft/utils';
import { CLOSE_DRAWING_EDITOR_TOPIC, DRAWING_MODIFICATION_TOPIC } from '../../../../busTopics';
import { AnnotationDrawing } from '../annotation-drawing';
import { ArrowDrawingTool, CircleDrawingTool, DrawingTool, DrawingToolType, FreeDrawingTool, RectangleDrawingTool, ShapeConfig } from '../toolbox';

@Component({
    components: {
        sAnnotationDrawing: AnnotationDrawing,
        sEnhancedTextInput: EnhancedTextInput,
        sMedia: Media,
        swatches: Swatches
    },
    props: ['annotation', 'annotationFieldName', 'annotationListFieldIndex', 'form', 'mediaUrl', 'breadcrumbObject'],
    template: require('./drawing-editor.html')
})
export class DrawingEditor extends ReactiveVue {

    public form: FormApi

    // Icons
    public backIcon = faArrowLeft;
    public circleIcon = faCircle;
    public arrowIcon = faLongArrowAltLeft;
    public curveIcon = faPen;
    public squareIcon = faSquare;
    public crossCircleIcon = faTimesCircle;
    public checkIcon = faCheck;
    public colorIcon = faPalette;
    public imageIcon = faImage;
    public loadingIcon = faCircleNotch;

    public annotation: DefectAnnotation;
    public annotationFieldName: string;
    public annotationListFieldIndex: number
    public mediaUrl: string;
    public breadcrumbObject: any;

    public image = new Image()
    public configImage: any = {};
    public $refs: { stage: any }

    public $thesaurusTermDataStore: ThesaurusTermDataStore;
    public $mediaTransferHelper: MediaTransferHelper;

    // Header
    private breadcrumb: string = "";

    // Drawing
    private readonly DEFAULT_STROKE_COLOR = "#f15f22";
    private strokeColor = this.DEFAULT_STROKE_COLOR;
    private currentToolType: DrawingToolType = DrawingToolType.CIRCLE
    private currentTool: DrawingTool = null as any;
    private selectedToolClass = "selectedTool"
    private toolBox: Map<DrawingToolType, DrawingTool> = new Map()
    private shapes: ShapeConfig[] = [];
    private container = null as any;
    private stage = null as any;
    private stageScaleFactor = 1;
    private stageOffset = { x: 0, y: 0 };
    private startClick: any = undefined;
    private currentShape: any = undefined;
    private isDrawing: boolean = false;
    private orientation: number = 1;

    private shapeIndex: number = 0;

    // Responsive computing default values
    private strokeWidth: number = 5;
    private arrowPointerWidth: number = 20;
    private arrowPointerLength: number = 20;
    private annotationLabelConfig = {
        fontSize: 18 as number,
        radius: 15 as number,
        strokeWidth: 2 as number
    }
    private editorOrientationClass = "";
    private canvasOrientationClass = "";

    // The ratios represent a relative size compared to the image's width, so that 
    // the apparent size of annotation and labels is the same for all kind of image dimensions
    private readonly STROKE_WIDTH_RATIO: number = 0.005;
    private readonly ANNOTATION_LABEL_RADIUS_RATIO = 0.02;
    private readonly ANNOTATION_LABEL_FONT_SIZE_RATIO = 0.02;
    private readonly ANNOTATION_LABEL_STROKE_WIDTH_RATIO = 0.002;
    private readonly ARROW_POINTER_LENGTH_RATIO = 0.02
    private readonly ARROW_POINTER_WIDTH_RATIO = 0.02

    private ready: boolean = false;

    // The factor used to limit the number of points in a free drawing
    private freeDrawingSimplificationFactor: number = 1.5;
    private readonly FREE_DRAWING_SIMPLIFICATION_FACTOR_RATIO = 0.002;

    private configKonva = {
        height: 50,
        width: 50,
    }

    private changeDrawingTool(tool: DrawingToolType) {
        this.currentToolType = tool
    }

    private get observations(): any[] {
        const obs = get(this.form.getState().values, `${this.annotationFieldName}.comments`)
        if (!obs) {
            this.observations = []
        }
        return get(this.form.getState().values, `${this.annotationFieldName}.comments`)
    }

    private set observations(value) {
        set(this.form.getState().values, `${this.annotationFieldName}.comments`, value)
    }

    /**
     * Allows to make the Konva stage fill the window properly
     */
    private resizeStage() {
        if (this.stage && this.container) {

            this.adaptCanvasSizeToImageOrientation()

            // Take orientation into account to retrieve real width of image (chrome 81 issue)
            const refDimension = this.orientation === 6 || this.orientation === 8 ? this.image.width : this.image.height

            // setting drawing values for responsive behavior
            this.strokeWidth = this.STROKE_WIDTH_RATIO * refDimension;
            this.arrowPointerLength = this.ARROW_POINTER_LENGTH_RATIO * refDimension;
            this.arrowPointerWidth = this.ARROW_POINTER_WIDTH_RATIO * refDimension;
            this.annotationLabelConfig.fontSize = this.ANNOTATION_LABEL_FONT_SIZE_RATIO * refDimension;
            this.annotationLabelConfig.radius = this.ANNOTATION_LABEL_RADIUS_RATIO * refDimension;
            this.annotationLabelConfig.strokeWidth = this.ANNOTATION_LABEL_STROKE_WIDTH_RATIO * refDimension;
            this.freeDrawingSimplificationFactor = this.FREE_DRAWING_SIMPLIFICATION_FACTOR_RATIO * refDimension;

            // Make editor display only when resizing stuff is done
            this.ready = true
        }
    }

    /**
     * Makes the canvas fit the contained image, taking the
     * image orientation into account
     */
    private adaptCanvasSizeToImageOrientation() {
        const containerSize = {
            height: this.container.clientHeight,
            width: this.container.clientWidth
        };
        let imageRatio = 1;
        switch (this.orientation) {
            // Vertical images
            case 6:
            case 8:
                {
                    this.stageScaleFactor = containerSize.height / this.image.width;
                    imageRatio = this.image.width / this.image.height;
                    this.stage.scale({ x: this.stageScaleFactor, y: this.stageScaleFactor })
                    this.configKonva.height = containerSize.height;
                    this.configKonva.width = this.configKonva.height / imageRatio;
                    break;
                }
            // Horizontal images
            default:
                {
                    // Vertical images with orientation 1 
                    if (this.image.height > this.image.width) {
                        this.stageScaleFactor = containerSize.height / this.image.height;
                        imageRatio = this.image.height / this.image.width;
                        this.stage.scale({ x: this.stageScaleFactor, y: this.stageScaleFactor })
                        this.configKonva.height = containerSize.height;
                        this.configKonva.width = this.configKonva.height / imageRatio;
                    } else {
                        this.stageScaleFactor = containerSize.width / this.image.width;
                        imageRatio = this.image.width / this.image.height;
                        this.stage.scale({ x: this.stageScaleFactor, y: this.stageScaleFactor })
                        this.configKonva.width = containerSize.width;
                        this.configKonva.height = this.configKonva.width / imageRatio;
                        break;
                    }
                }
        }
    }

    private fetchMedia() {
        // Use local image that is newly imported
        if (this.mediaUrl) {
            this.setupMedia(this.mediaUrl)
        }
        // Retrieve image from id in api
        else {
            this.subscribeTo(this.$mediaTransferHelper.downloadMedia(this.annotation.media, "proxy"),
                mediaSrc => {
                    this.setupMedia(mediaSrc.dataUrl)
                },
                'media',
            );
        }
    }

    private setupMedia(mediaSrc) {
        this.image.src = mediaSrc
        this.image.onload = () => {
            this.fixImageOrientation({
                height: this.image.height,
                image: this.image,
                width: this.image.width,
                x: 0,
                y: 0,
            })

            this.stage = this.$refs.stage.getStage()
            this.container = this.stage.container();

            // Force canvas resizing and redrawing after Vue render
            this.$nextTick().then(() => this.resizeStage())
            // Listening to window resize event to trigger stage redrawing
            window.addEventListener('resize', this.resizeStage)
        }
    }

    /**
     * Adapts the orientation of the image according to the
     * EXIF orientation metadata
     * @param baseImageConfig 
     */
    // tslint:disable-next-line:member-ordering
    public async fixImageOrientation(baseImageConfig) {
        this.orientation = isImageOrientationAutoManaged() ? 1 : await getImageOrientation(baseImageConfig.image)
        switch (this.orientation) {
            case 3:
                this.configImage = {
                    ...baseImageConfig,

                    offsetX: baseImageConfig.width,
                    offsetY: baseImageConfig.height,
                    rotation: 180,
                }
                this.editorOrientationClass = "drawing-editor-h"
                this.canvasOrientationClass = "drawing-canvas-h"
                break;
            case 6:
                this.configImage = {
                    ...baseImageConfig,

                    offsetX: 0,
                    offsetY: baseImageConfig.height,
                    rotation: 90,
                }
                this.editorOrientationClass = "drawing-editor-v"
                this.canvasOrientationClass = "drawing-canvas-v"
                break;
            case 8:
                this.configImage = {
                    ...baseImageConfig,

                    offsetX: baseImageConfig.width,
                    offsetY: 0,
                    rotation: -90,
                }
                this.editorOrientationClass = "drawing-editor-v"
                this.canvasOrientationClass = "drawing-canvas-v"
                break;

            default:
                if (this.image.height > this.image.width) {
                    this.configImage = baseImageConfig;
                    this.editorOrientationClass = "drawing-editor-v"
                    this.canvasOrientationClass = "drawing-canvas-v"
                } else {
                    this.configImage = baseImageConfig;
                    this.editorOrientationClass = "drawing-editor-h"
                    this.canvasOrientationClass = "drawing-canvas-h"
                }
                break;
        }
    }

    private closeDrawingEditor(save: boolean) {
        window.removeEventListener('resize', this.resizeStage);
        // Replacing empty fields by '...' to prevent them to be erased in the api
        this.observations = this.observations.map(value => {
            if (!getTranslatedString(value, this.$i18n.locale)) {
                set(value, `translatedString.${this.$i18n.locale}`, '...')
            }
            return value;
        })
        this.emitOnBus(CLOSE_DRAWING_EDITOR_TOPIC, { saveReport: save })
    }

    private onTouchStart(event) {
        if (event.currentTarget._pointerPositions) {
            this.startClick = {}
            this.startClick.x = (event.currentTarget._changedPointerPositions[0].x - this.stageOffset.x) / this.stageScaleFactor
            this.startClick.y = (event.currentTarget._changedPointerPositions[0].y - this.stageOffset.y) / this.stageScaleFactor

            this.chooseTool(this.currentToolType);
            this.currentShape = this.currentTool.addShape(this.startClick.x, this.startClick.y, this.shapeIndex, this.strokeColor)
            this.shapes.push(this.currentShape)
            this.addObservation()
            this.shapeIndex++;
            this.isDrawing = true;
        } else {
            this.startClick = undefined
        }
    }

    private onTouchMove(event) {
        if (this.isDrawing && event.currentTarget._changedPointerPositions) {

            const currentX = (event.currentTarget._changedPointerPositions[0].x - this.stageOffset.x) / this.stageScaleFactor
            const currentY = (event.currentTarget._changedPointerPositions[0].y - this.stageOffset.y) / this.stageScaleFactor

            this.currentTool.onTouchMove(this.currentShape, currentX, currentY, this.startClick.x, this.startClick.y);
        }
    }

    private onTouchEnd(event) {
        if (this.startClick && event.currentTarget._changedPointerPositions) {

            const endX = (event.currentTarget._changedPointerPositions[0].x - this.stageOffset.x) / this.stageScaleFactor
            const endY = (event.currentTarget._changedPointerPositions[0].y - this.stageOffset.y) / this.stageScaleFactor
            this.currentTool.onTouchEnd(this.currentShape, endX, endY, this.startClick.x, this.startClick.y)
        }
        this.isDrawing = false;
    }

    private chooseTool(toolType: DrawingToolType) {
        if (!this.toolBox.get(toolType)) {
            switch (toolType) {
                case DrawingToolType.ARROW:
                    this.toolBox.set(toolType, new ArrowDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR, this.arrowPointerLength, this.arrowPointerWidth))
                    break;
                case DrawingToolType.CIRCLE:
                    this.toolBox.set(toolType, new CircleDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR))
                    break;
                case DrawingToolType.RECTANGLE:
                    this.toolBox.set(toolType, new RectangleDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR))
                    break;
                case DrawingToolType.FREE_DRAWING:
                    this.toolBox.set(toolType, new FreeDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR, true, this.freeDrawingSimplificationFactor))
                    break;
                default:
                    this.toolBox.set(toolType, new CircleDrawingTool(this.strokeWidth, this.DEFAULT_STROKE_COLOR));
                    break;
            }
        }
        const tool = this.toolBox.get(toolType)
        if (tool) {
            this.currentTool = tool;
        }
    }

    private getObservationKey(index) {
        return this.shapes[index].id
    }

    private getObservationFieldName(index) {
        return `${this.annotationFieldName}.comments.${index}.translatedString.${this.$i18n.locale}`
    }

    private get titleFieldName() {
        return `${this.annotationFieldName}.title.translatedString.${this.$i18n.locale}`;
    }

    private get annotationTitle() {
        return get(this.annotation.title, `translatedString.${this.$i18n.locale}`);
    }

    private addObservation() {
        this.observations.push("")
    }

    private deleteObservation(index) {
        this.observations.splice(index, 1);
        this.shapes.splice(index, 1);
    }

    private isToolSelected(tool: DrawingToolType) {
        return this.currentToolType === tool;
    }

    private validate() {
        this.closeDrawingEditor(true);
    }

    private async buildBreadcrumb() {
        const observationName = await this.getThesaurusTermNameIfExists(this.breadcrumbObject.observationId)
        const materialName = await this.getThesaurusTermNameIfExists(this.breadcrumbObject.materialId)
        const alterationName = await this.getThesaurusTermNameIfExists(this.breadcrumbObject.alterationId)
        this.breadcrumb = "";
        this.breadcrumb += observationName || "";
        this.breadcrumb += ((observationName && materialName) || (observationName && alterationName) ? " > " : "")
        this.breadcrumb += materialName || ""
        this.breadcrumb += (materialName && alterationName ? " > " : "")
        this.breadcrumb += alterationName || ""
    }

    private async getThesaurusTermNameIfExists(id: string) {

        if (id) {
            const thesaurusTerm = await this.$thesaurusTermDataStore.fetchOne(id).pipe(first()).toPromise();
            if (thesaurusTerm) {
                return getTranslatedString(
                    preferredTerm(thesaurusTerm),
                    this.$i18n.locale
                )
            }
        }
        return ''
    }

    private unsubscribe() {
        this.unsubscribeFrom('annotationTitle');
        this.unsubscribeFrom('media');
    }

    @Watch('annotation', { immediate: true })
    private onAnnotationChange() {
        this.unsubscribe()
        this.fetchMedia();
        this.buildBreadcrumb()
        // getting shapes
        this.shapes = JSON.parse(this.annotation.svg)
        if (!this.shapes) {
            this.shapes = []
        }
        this.renumberShapes()
        this.shapeIndex = this.shapes.length

        // getting observations
        const strAnnotationComments = this.annotation.comments ? this.annotation.comments.map(trs => getTranslatedString(trs, this.$i18n.locale)) : []
        this.observations = strAnnotationComments || [];
    }

    /**
     * Renumbers all shapes from 0 to avoid conflicts in indices
     */
    private renumberShapes() {
        this.shapes.map((shape, index) => {
            this.chooseTool(shape.type)
            shape.id = this.currentTool.generateShapeId(index)
        })
        this.toolBox.clear()
    }

    @Watch('shapes', { deep: true })
    private onShapesChange() {
        // Update shapes
        set(this.form.getState().values, `${this.annotationFieldName}.svg`, JSON.stringify(this.shapes))
    }
}